import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useT } from '../i18n/locale-context';
import { isPlaceholderEmail } from '../lib/user-display';
import { VerifyEmailAction } from './verify-email-action';

export type EmailConfirmOutcome = 'confirmed' | 'already' | 'expired' | 'invalid';

const OUTCOMES: readonly EmailConfirmOutcome[] = ['confirmed', 'already', 'expired', 'invalid'];

/** The `?emailConfirm=` value `GET /auth/email/confirm` redirects with, or null. */
export function readEmailConfirmOutcome(search: string): EmailConfirmOutcome | null {
  const raw = new URLSearchParams(search).get('emailConfirm');
  return OUTCOMES.includes(raw as EmailConfirmOutcome) ? (raw as EmailConfirmOutcome) : null;
}

/**
 * Where the «Подтвердить почту» button in a letter lands.
 *
 * `GET /auth/email/confirm` confirms the address and redirects to the home
 * page with `?emailConfirm=<outcome>`. This turns that into one line at the top
 * of the page, then strips the parameter so a reload or a shared link does not
 * repeat it.
 *
 * - confirmed / already → «Почта подтверждена». The same sentence for both: a
 *   second tap on the same button asked for the same thing.
 * - expired / invalid → say so and offer the CODE in place (`VerifyEmailAction`)
 *   when the reader is signed in on this device with an unconfirmed address;
 *   otherwise tell them where the code lives. Never a dead end.
 *
 * Client-only (an effect reads `window.location`), so the prerendered home page
 * is byte-identical with or without the parameter.
 */
export function EmailConfirmNotice() {
  const { user, status, refresh } = useAuth();
  const t = useT();
  const [outcome, setOutcome] = useState<EmailConfirmOutcome | null>(null);

  useEffect(() => {
    const found = readEmailConfirmOutcome(window.location.search);
    if (!found) return;
    setOutcome(found);
    const url = new URL(window.location.href);
    url.searchParams.delete('emailConfirm');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  // A signed-in tab must stop showing «подтвердите почту» everywhere else on
  // the page — the session payload still says `emailVerified: false`.
  useEffect(() => {
    if ((outcome === 'confirmed' || outcome === 'already') && status === 'authed') {
      void refresh().catch(() => undefined);
    }
    // `refresh` is stable; re-running on `status` covers a session that
    // resolves after the notice mounted.
  }, [outcome, status, refresh]);

  if (!outcome) return null;

  const ok = outcome === 'confirmed' || outcome === 'already';
  const canTypeCode =
    !ok &&
    status === 'authed' &&
    !!user?.email &&
    !isPlaceholderEmail(user.email) &&
    user.emailVerified === false;

  return (
    <div
      role="status"
      className={`mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg px-3 py-2 text-[13px] ${
        ok ? 'bg-brand/10 text-ink' : 'bg-amber-50 text-amber-900'
      }`}
    >
      <span className="min-w-0 font-semibold">
        {ok
          ? `✓ ${t('emailConfirm.ok')}`
          : t(outcome === 'expired' ? 'emailConfirm.expired' : 'emailConfirm.invalid')}
      </span>
      {canTypeCode && user?.email ? (
        <VerifyEmailAction email={user.email} showWhy={false} />
      ) : !ok && status !== 'authed' ? (
        <span className="basis-full text-[12.5px]">{t('emailConfirm.signIn')}</span>
      ) : null}
      <button
        type="button"
        onClick={() => setOutcome(null)}
        aria-label={t('emailConfirm.close')}
        className={`${canTypeCode ? '' : 'ml-auto'} shrink-0 rounded-md px-1.5 text-[15px] leading-none text-ink-muted hover:text-ink`}
      >
        ✕
      </button>
    </div>
  );
}
